/**
 * IntensityFloorTrail — A vertex-colored plane on the tunnel floor
 * that shows intensity as a blue→cyan→green→amber→red gradient.
 * Sits just above the dark floor plane (Y=0.002) and below the beat grid.
 * Single draw call, ~400 vertices — negligible GPU cost.
 */

import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore } from '../../store/editorStore';
import { intensityToRGB } from '../../heatmap/intensityEngine';
import {
  BEAT_SPACING,
  TUNNEL_WIDTH_EXPORT as TUNNEL_WIDTH,
  TUNNEL_LENGTH_EXPORT as TUNNEL_LENGTH,
  VISIBLE_BEATS_BEHIND,
} from './TunnelScene';

const SEGMENTS_Z = 200;
const SEGMENTS_X = 1;
const FLOOR_Y = 0.002;
const TRAIL_OPACITY = 0.35;

export default function IntensityFloorTrail() {
  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const intensityData = useEditorStore(s => s.intensityData);
  const meshRef = useRef<THREE.Mesh>(null);
  const lastBeatTick = useRef(-1);

  // Build the geometry once — a flat plane subdivided along Z
  const { geometry, colorAttr } = useMemo(() => {
    const nearZ = VISIBLE_BEATS_BEHIND * 4; // some extra behind
    const totalLen = TUNNEL_LENGTH + nearZ;

    const geo = new THREE.PlaneGeometry(TUNNEL_WIDTH, totalLen, SEGMENTS_X, SEGMENTS_Z);
    // Rotate to be flat on the floor (PlaneGeometry faces +Z by default)
    geo.rotateX(-Math.PI / 2);
    // Position so it extends from behind player to far tunnel
    geo.translate(0, 0, -TUNNEL_LENGTH / 2);

    // Create color attribute — 3 floats per vertex (RGB)
    const vertCount = geo.getAttribute('position').count;
    const colors = new Float32Array(vertCount * 3);
    // Initialize to dark (0, 0, 0)
    colors.fill(0);
    const attr = new THREE.BufferAttribute(colors, 3);
    geo.setAttribute('color', attr);

    return { geometry: geo, colorAttr: attr };
  }, []);

  // Update vertex colors based on current beat + intensity data
  useFrame(() => {
    if (!heatmapEnabled || !intensityData || !colorAttr) return;

    // Use rawBeat — same reference frame as note.time_beats (no offset subtraction)
    const store = useEditorStore.getState();
    const bpm = store.beatmap?.bpm || 120;
    const currentBeat = (store.playback.currentTime / 60) * bpm;
    // Only update at ~10× per beat resolution to save CPU
    const tick = Math.floor(currentBeat * 10);
    if (tick === lastBeatTick.current) return;
    lastBeatTick.current = tick;

    const posAttr = geometry.getAttribute('position');
    const colors = colorAttr.array as Float32Array;
    const vertCount = posAttr.count;
    const { beatIntensity, totalBeats } = intensityData;

    for (let i = 0; i < vertCount; i++) {
      const z = posAttr.getZ(i);
      // Z is negative going into the tunnel; convert to beat offset
      // Note position formula: z = -(beat - currentBeat) * BEAT_SPACING
      // So beat = currentBeat + (-z / BEAT_SPACING)
      const spacing = BEAT_SPACING || 2;
      const beatAtVertex = currentBeat + (-z / spacing);
      const beatIdx = Math.floor(beatAtVertex);

      let intensity = 0;
      if (beatIdx >= 0 && beatIdx < totalBeats) {
        intensity = beatIntensity[beatIdx];
      } else if (beatIdx >= totalBeats && totalBeats > 0) {
        intensity = beatIntensity[totalBeats - 1];
      }

      const [r, g, b] = intensityToRGB(intensity);
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
    }

    colorAttr.needsUpdate = true;
  });

  if (!heatmapEnabled || !intensityData) return null;

  return (
    <mesh ref={meshRef} geometry={geometry} position={[0, FLOOR_Y, 0]}>
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={TRAIL_OPACITY}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
